/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/ban-ts-comment */
/* eslint-disable @typescript-eslint/naming-convention */
import React from 'react'
import { trackInternalEvent } from '@rio/tracking'
import { useMediaQueries } from 'builder/hooks/useMediaQueries'
import { useSyncedQueryParams } from 'builder/hooks/useSyncedQueryParams'
import { TabItem, ContentContainer } from './styles'
import CategoryDrawer from './components/CategoryDrawer'
import CategogryDropdown from './components/CategoryDropdown'
import CategoryTabsList from './components/CategoryTabList'

type ContextProps = {
  activeTab: CategoryIds
  setActiveTab: (tab: CategoryIds) => void
}

const Context = React.createContext({} as ContextProps)

export function useCategoryTabsContext() {
  const context = React.useContext(Context)
  if (!context) {
    throw new Error('useCategoryTabsContext must be used within a CategoryTabsProvider')
  }
  return context
}

export type CategoryIds = 'resumes' | 'jobs' | 'interview' | 'learning'
export const DEFAULT_ACTIVE_TAB: CategoryIds = 'resumes'

export default function CategoryTabs(props: {
  children: React.ReactNode
  defaultActive?: CategoryIds
}) {
  const storedCategoryTab = localStorage.getItem('lastCategoryTab') as CategoryIds | null
  const defaultCateogoryTab = storedCategoryTab ?? props.defaultActive ?? DEFAULT_ACTIVE_TAB
  const { value1: rawActiveTab, setValue1: setActiveTab } = useSyncedQueryParams(
    'categoryTab',
    defaultCateogoryTab,
    'serviceId',
    '',
  )

  const activeTab = rawActiveTab as CategoryIds
  return <Context.Provider value={{ activeTab, setActiveTab }}>{props.children}</Context.Provider>
}

const List = ({ children }: { children: React.ReactNode }) => {
  const { isTablet, isLaptop, isPhone } = useMediaQueries()
  const childArray = React.Children.toArray(children)

  const items = childArray
    .filter(React.isValidElement<{ tabId: CategoryIds; children: React.ReactNode }>)
    .map(el => ({
      id: el.props.tabId,
      label: typeof el.props.children === 'string' ? el.props.children : String(el.props.children),
      element: el,
    }))

  if (isPhone) {
    return <CategoryDrawer items={items} />
  }

  if (isTablet || isLaptop) {
    return <CategogryDropdown items={items} />
  }

  return <CategoryTabsList items={items} />
}

const Item = (props: { children: React.ReactNode; tabId: CategoryIds }) => {
  const { activeTab, setActiveTab } = useCategoryTabsContext()

  const handleClick = () => {
    trackInternalEvent('click_top_nav', { category: props.tabId })
    setActiveTab(props.tabId)
    localStorage.setItem('lastCategoryTab', props.tabId)
  }

  return (
    <TabItem isActive={activeTab === props.tabId} onClick={handleClick}>
      {props.children}
    </TabItem>
  )
}

const Content = (props: { children: React.ReactNode; tabId: CategoryIds }) => {
  const { activeTab } = useCategoryTabsContext()

  if (activeTab !== props.tabId) return null
  return <ContentContainer id={`${props.tabId}-content`}>{props.children}</ContentContainer>
}

CategoryTabs.List = React.memo(List)
CategoryTabs.Item = React.memo(Item)
CategoryTabs.Content = React.memo(Content)
